import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Schema } from "effect";
import { toast } from "sonner";
import { errorService } from "@ascleo/client/services/error.service";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Loader2,
  LogOut,
  QrCode,
  TriangleAlert,
} from "lucide-react";
import { AscleoLogo } from "@ascleo/ui/components/ui/ascleo-logo";
import { Button } from "@ascleo/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@ascleo/ui/components/ui/collapsible";
import { FieldError } from "@ascleo/ui/components/ui/field";
import { getFieldErrorMessage } from "@ascleo/ui/components/ui/form-field";
import { Input } from "@ascleo/ui/components/ui/input";
import { Label } from "@ascleo/ui/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ascleo/ui/components/ui/select";
import { Switch } from "@ascleo/ui/components/ui/switch";
import {
  audioEmitter,
  parseBrowserSetupPayload,
  type PlatformAdapter,
  type PracticeSettings,
  type StylingSettings,
} from "@ascleo/frontdesk-shared";
import {
  enrollDevice,
  getGatewaySession,
  replaceToken,
  revokeDevice,
  testToken,
  type GatewaySession,
  type HostedReadiness,
} from "../lib/gateway-client";
import { startBrowserSetupScanner } from "../lib/browser-setup-scanner";
import { useIsFramed } from "../lib/is-framed-context";
import { evaluateBrowserSetup } from "../lib/setup-readiness";
import {
  WebSettingsFormSchema,
  type WebSettingsFormData,
} from "../lib/web-settings-form.schema";
import { validateSettingsPinMask } from "@ascleo/frontdesk-shared/settings-pin-input";
import { BrowserSetupScannerDialog } from "./BrowserSetupScannerDialog";

interface BrowserSettingsPageProps {
  readonly adapter: PlatformAdapter;
  readonly session: GatewaySession;
  readonly hostedReadiness: HostedReadiness | null;
  readonly practiceSettings: PracticeSettings;
  readonly stylingSettings: StylingSettings;
  readonly locked: boolean;
  readonly onSessionChange: (session: GatewaySession) => void;
  readonly onPracticeChange: (settings: PracticeSettings) => void;
  readonly onStylingChange: (settings: StylingSettings) => void;
  readonly onRequestUnlock: () => void;
  readonly onRefreshHostedReadiness: () => Promise<unknown>;
  readonly onOpenKiosk: () => void;
}

interface CardReadersResponse {
  readonly cardReaders: readonly BrowserCardReader[];
  readonly error?: string;
}

interface BrowserCardReader {
  readonly id: string;
  readonly name: string;
  readonly workplaceName?: string | null;
  readonly mandantName?: string | null;
}

interface BrowserTerminalOption {
  readonly id: string;
  readonly name: string;
  readonly subtitle?: string;
}

interface IssueCopy {
  readonly title: string;
  readonly action: string;
  readonly fieldId: string | null;
}

const issueCopy: Record<string, IssueCopy> = {
  missing_gateway_session: {
    title: "Browser mit dem Gateway verbinden",
    action: "Gateway verbinden",
    fieldId: null,
  },
  missing_gateway_enrollment: {
    title: "Gerät registrieren",
    action: "Gerät registrieren",
    fieldId: null,
  },
  gateway_not_ready: {
    title: "Gateway-Einrichtung abschließen",
    action: "Einrichtung abschließen",
    fieldId: null,
  },
  device_revoked: {
    title: "Gerät erneut registrieren",
    action: "Erneut registrieren",
    fieldId: null,
  },
  session_expired: {
    title: "Geschützte Sitzung erneuern",
    action: "Sitzung erneuern",
    fieldId: null,
  },
  missing_insuite_url: {
    title: "Bridge-Adresse einfügen",
    action: "Bridge-Adresse eingeben",
    fieldId: "insuite-url",
  },
  missing_server_token: {
    title: "Server-Token hinzufügen",
    action: "Server-Token eingeben",
    fieldId: "soltoken",
  },
  token_verification_failed: {
    title: "Server-Token oder Bridge-Verbindung prüfen",
    action: "Prüfung wiederholen",
    fieldId: null,
  },
  hosted_general_api_unavailable: {
    title: "Allgemeine API-Erreichbarkeit prüfen",
    action: "Verbindung prüfen",
    fieldId: null,
  },
  missing_practice_name: {
    title: "Praxisnamen festlegen",
    action: "Praxisnamen eingeben",
    fieldId: "practice-name",
  },
  missing_device_name: {
    title: "Kiosknamen festlegen",
    action: "Gerätenamen eingeben",
    fieldId: "device-name",
  },
};

/** Focus the device PIN and explain that the form was refused. */
function revealInvalidDeviceSettings(): void {
  document.getElementById("settings-pin")?.focus();
  errorService.report(new Error("Device settings form is invalid"), {
    layer: "form",
    source: "frontdesk-web.settings.device",
    userMessage: "Bitte die Eingaben prüfen.",
  });
}

const readerWarningCopy: Record<"missing" | "unavailable", string> = {
  missing:
    "Kein Kartenlesegerät ausgewählt. Die Kartenerkennung erfolgt über die manuelle Suche.",
  unavailable: "Gespeichertes Kartenlesegerät ist derzeit nicht verfügbar.",
};

const hostedWarningCopy: Record<
  "telematik_unavailable" | "selected_card_reader_unavailable" | "medical_card_unavailable",
  string
> = {
  telematik_unavailable:
    "Die TI ist derzeit nicht verfügbar. Kartengestützte Funktionen bleiben vorübergehend eingeschränkt.",
  selected_card_reader_unavailable:
    "Das ausgewählte Kartenlesegerät ist derzeit nicht erreichbar. Kartengestützte Funktionen bleiben vorübergehend eingeschränkt.",
  medical_card_unavailable:
    "Es wurde keine SMC-B- oder HBA-Karte erkannt. Rezept- und eGK-sensitive Funktionen bleiben vorübergehend eingeschränkt.",
};

/**
 * Returns true when the Advanced disclosure should render open on first mount
 * because the session is in a state that requires token maintenance action.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const open = shouldAdvancedStartOpen(session)
 * ```
 */
export function shouldAdvancedStartOpen(session: GatewaySession): boolean {
  if (session.enrolled && !session.token.configured) {
    return true;
  }
  if (session.token.lastVerificationResult === "failed") {
    return true;
  }
  if (session.expired) {
    return true;
  }
  if (session.revoked) {
    return true;
  }
  return false;
}

/**
 * Browser-safe setup/settings screen for gateway-backed FrontDesk deployments.
 *
 * @since 0.0.0
 * @example
 * ```tsx
 * <BrowserSettingsPage onOpenKiosk={openKiosk} />
 * ```
 */
export function BrowserSettingsPage({
  adapter,
  session,
  hostedReadiness,
  practiceSettings,
  stylingSettings,
  locked,
  onSessionChange,
  onPracticeChange,
  onStylingChange,
  onRefreshHostedReadiness,
  onOpenKiosk,
}: BrowserSettingsPageProps) {
  const isFramed = useIsFramed();
  const [insuiteRestUrl, setInsuiteRestUrl] = useState(
    session.insuiteRestUrl ?? "",
  );
  const [solToken, setSolToken] = useState("");
  const [replacementToken, setReplacementToken] = useState("");
  // Device configuration is a schema-validated form: the shared PIN rule refuses the
  // default 0000 before anything is written, so the browser kiosk can never persist a
  // PIN its own unlock screen refuses. Enrollment and token handling stay outside it.
  const persistDeviceSettingsRef = useRef<() => Promise<void>>(async () => undefined);
  const defaultValues: WebSettingsFormData = {
    practiceName: session.practiceName ?? practiceSettings.practiceName,
    deviceName: session.deviceName ?? practiceSettings.deviceName ?? "",
    welcomeMessage: practiceSettings.welcomeMessage,
    schedulerEnabled: practiceSettings.schedulerAvailableInSelfService ?? true,
    prescriptionEnabled:
      practiceSettings.prescriptionServiceAvailableInSelfService ?? false,
    selectedTerminalId: practiceSettings.selectedTerminalId ?? "",
    pin: stylingSettings.pin,
    voiceOverVolume: stylingSettings.voiceOverVolume,
    languageSelectionOnSessionStart: stylingSettings.languageSelectionOnSessionStart ?? false,
  };
  const form = useForm({
    defaultValues,
    validators: {
      onChange: Schema.toStandardSchemaV1(WebSettingsFormSchema),
      onSubmitAsync: async ({ value }) => {
        const issue = await validateSettingsPinMask(value.pin, () => adapter.isPinConfigured());
        return issue === undefined ? undefined : { fields: { pin: { message:
          issue === "unavailable" ? "Die gespeicherte PIN konnte nicht geprüft werden." : "Bitte eine eigene PIN aus 4 bis 8 Ziffern setzen.",
        } } };
      },
    },
    onSubmitInvalid: () => {
      revealInvalidDeviceSettings();
    },
    onSubmit: async () => {
      await persistDeviceSettingsRef.current();
    },
  });
  const {
    practiceName,
    deviceName,
    welcomeMessage,
    schedulerEnabled,
    prescriptionEnabled,
    selectedTerminalId,
    pin,
    voiceOverVolume,
    languageSelectionOnSessionStart,
  } = useStore(form.store, (state) => state.values);
  const setPracticeName = useCallback(
    (value: string) => form.setFieldValue("practiceName", value),
    [form],
  );
  const setDeviceName = useCallback(
    (value: string) => form.setFieldValue("deviceName", value),
    [form],
  );
  const setSelectedTerminalId = useCallback(
    (value: string) => form.setFieldValue("selectedTerminalId", value),
    [form],
  );
  const [terminals, setTerminals] = useState<readonly BrowserTerminalOption[]>(
    [],
  );
  const [isLoadingTerminals, setIsLoadingTerminals] = useState(false);
  const [isWorking, setIsWorking] = useState<string | null>(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannerAttempt, setScannerAttempt] = useState(0);
  const [scannerState, setScannerState] = useState<
    "idle" | "requesting" | "scanning" | "error" | "success"
  >("idle");
  const [scannerMessage, setScannerMessage] = useState(
    "Kamera öffnen, um den Bridge-QR-Code zu scannen.",
  );
  const [lastImportSource, setLastImportSource] = useState<
    "versioned" | "legacy" | null
  >(null);
  const [scannerVideoElement, setScannerVideoElement] =
    useState<HTMLVideoElement | null>(null);
  const [isConnectionEditing, setIsConnectionEditing] = useState(
    !session.enrolled,
  );

  const blockersRef = useRef<HTMLElement | null>(null);

  const setupStatus = useMemo(
    () => evaluateBrowserSetup(session, practiceSettings, hostedReadiness),
    [hostedReadiness, session, practiceSettings],
  );

  // A saved unavailable reader is a displayed value, not a selectable option.
  // Keep the option list independent of selection: removing a synthetic option
  // during Base UI's selection reconciliation can clear the newly chosen reader.
  const selectedReaderLabel = terminals.find(
    (terminal) => terminal.id === selectedTerminalId,
  )?.name ?? "Gespeichertes Lesegerät";

  const localReaderWarning = useMemo(() => {
    if (!session.enrolled || !session.token.configured) {
      return null;
    }

    if (!selectedTerminalId) {
      return {
        code: "selected_card_reader_unavailable" as const,
        message: readerWarningCopy["missing"],
      };
    }

    if (isLoadingTerminals) {
      return null;
    }

    if (!terminals.some((terminal) => terminal.id === selectedTerminalId)) {
      return {
        code: "selected_card_reader_unavailable" as const,
        message: readerWarningCopy["unavailable"],
      };
    }

    return null;
  }, [
    isLoadingTerminals,
    selectedTerminalId,
    session.enrolled,
    session.token.configured,
    terminals,
  ]);

  const blockers = useMemo(
    () =>
      setupStatus.blockers.map((code) => ({
        code,
        ...(issueCopy[code] ?? {
          title: code,
          action: code,
          fieldId: null,
        }),
      })),
    [setupStatus.blockers],
  );

  const warnings = useMemo(() => {
    const nextWarnings = setupStatus.warnings
      .filter(
        (code) =>
          code !== "selected_card_reader_unavailable" || !localReaderWarning,
      )
      .map((code) => ({
        code,
        message: hostedWarningCopy[
          code as keyof typeof hostedWarningCopy
        ] ?? code,
      }));

    if (localReaderWarning) {
      nextWarnings.unshift(localReaderWarning);
    }

    return nextWarnings;
  }, [localReaderWarning, setupStatus.warnings]);

  const firstIssue = blockers[0] ?? null;

  const statusState: "ready" | "warning" | "blocked" = setupStatus.ready
    ? warnings.length > 0
      ? "warning"
      : "ready"
    : "blocked";
  const statusLabel =
    statusState === "ready"
      ? "Einsatzbereit"
      : statusState === "warning"
        ? "Mit Hinweis einsatzbereit"
        : `${blockers.length} zu beheben`;

  useEffect(function syncSessionIntoLocalState() {
    setInsuiteRestUrl(session.insuiteRestUrl ?? "");
    setPracticeName(session.practiceName ?? practiceSettings.practiceName);
    setDeviceName(session.deviceName ?? practiceSettings.deviceName ?? "");
    setSelectedTerminalId(practiceSettings.selectedTerminalId ?? "");
    setIsConnectionEditing((current) =>
      session.enrolled ? current : true,
    );
  }, [session, practiceSettings, setDeviceName, setPracticeName, setSelectedTerminalId]);

  const persistNonSecretSettings = useCallback(
    async (
      nextSession: GatewaySession,
      // Values validated by the device-settings form. Callers that await
      // something first (enrollment) pass the snapshot they validated, so a
      // later edit cannot be written behind the validation boundary.
      validated?: WebSettingsFormData,
    ): Promise<void> => {
      const values: WebSettingsFormData = validated ?? {
        practiceName,
        deviceName,
        welcomeMessage,
        schedulerEnabled,
        prescriptionEnabled,
        selectedTerminalId,
        pin,
        voiceOverVolume,
        languageSelectionOnSessionStart,
      };
      const nextPractice: PracticeSettings = {
        ...practiceSettings,
        bridgeUrl: practiceSettings.bridgeUrl,
        solToken: undefined,
        practiceName: nextSession.practiceName ?? values.practiceName,
        deviceName: nextSession.deviceName ?? values.deviceName,
        welcomeMessage: values.welcomeMessage,
        selectedTerminalId: values.selectedTerminalId || undefined,
        schedulerAvailableInSelfService: values.schedulerEnabled,
        prescriptionServiceAvailableInSelfService: values.prescriptionEnabled,
      };
      const nextStyling: StylingSettings = {
        ...stylingSettings,
        pin: values.pin,
        voiceOverVolume: values.voiceOverVolume,
        languageSelectionOnSessionStart: values.languageSelectionOnSessionStart ?? false,
      };

      await Promise.all([
        adapter.setPracticeSettings(nextPractice),
        adapter.setStylingSettings(nextStyling),
      ]);
      audioEmitter.emit("audio:set-volume", values.voiceOverVolume);
      onPracticeChange(nextPractice);
      onStylingChange(nextStyling);
    },
    [
      adapter,
      deviceName,
      onPracticeChange,
      onStylingChange,
      pin,
      practiceName,
      practiceSettings,
      prescriptionEnabled,
      schedulerEnabled,
      selectedTerminalId,
      stylingSettings,
      languageSelectionOnSessionStart,
      voiceOverVolume,
      welcomeMessage,
    ],
  );

  const loadCardReaders = useCallback(
    async (options?: { readonly silent?: boolean }) => {
      if (!session.enrolled || !session.token.configured) {
        setTerminals([]);
        return;
      }

      setIsLoadingTerminals(true);
      try {
        const response = await fetch("/api/frontdesk/proxy/api/card-readers", {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          credentials: "include",
          cache: "no-store",
        });

        const payload = (await response.json().catch(() => ({}))) as
          | CardReadersResponse
          | Record<string, unknown>;

        if (!response.ok) {
          throw new Error(readGatewayMessage(payload, response.status));
        }

        const readers = Array.isArray(payload.cardReaders)
          ? payload.cardReaders.map(toTerminalOption)
          : [];

        setTerminals(readers);
      } catch (error) {
        setTerminals([]);
        if (!options?.silent) {
          toast.error(
            error instanceof Error
              ? error.message
              : "Kartenlesegeräte konnten nicht geladen werden",
          );
        }
      } finally {
        setIsLoadingTerminals(false);
      }
    },
    [session.enrolled, session.token.configured],
  );

  useEffect(function loadCardReadersOnSessionReady() {
    if (!session.enrolled || !session.token.configured) {
      setTerminals([]);
      return;
    }

    void loadCardReaders({ silent: true });
  }, [
    loadCardReaders,
    session.deviceId,
    session.enrolled,
    session.token.configured,
  ]);

  useEffect(function startScannerWhenOpen() {
    if (!isScannerOpen || !scannerVideoElement) {
      return;
    }

    let cancelled = false;
    let handle: { stop: () => void } | null = null;

    setScannerState("requesting");
    setScannerMessage("Kamerazugriff wird angefordert…");

    void startBrowserSetupScanner({
      video: scannerVideoElement,
      onCameraReady: () => {
        if (cancelled) {
          return;
        }

        setScannerState("scanning");
        setScannerMessage("Kamera auf den Bridge-QR-Code richten.");
      },
      onDecode: (rawValue) => {
        if (cancelled) {
          return;
        }

        handle?.stop();
        const result = parseBrowserSetupPayload(rawValue);
        if (!result.ok) {
          setScannerState("error");
          setScannerMessage(
            `${result.error} Werte alternativ manuell eingeben.`,
          );
          toast.error(result.error);
          return;
        }

        setInsuiteRestUrl(result.value.insuiteRestUrl);
        // A Bridge setup QR carries no SOLTOKEN; keep what is already entered instead of clearing it.
        if (result.value.solToken !== "") setSolToken(result.value.solToken);
        setLastImportSource(result.value.source);
        setScannerState("success");
        setScannerMessage(
          "Bridge-Einrichtung übernommen. Felder prüfen und „Registrieren“ drücken.",
        );
        toast.success("Bridge-Einrichtung in das Formular übernommen");
        setIsScannerOpen(false);
      },
    })
      .then((nextHandle) => {
        if (cancelled) {
          nextHandle.stop();
          return undefined;
        }

        handle = nextHandle;
        return undefined;
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setScannerState("error");
        setScannerMessage(
          `${getScannerErrorMessage(error)} Werte alternativ manuell eingeben.`,
        );
      });

    return function stopScanner() {
      cancelled = true;
      handle?.stop();
    };
  }, [isScannerOpen, scannerAttempt, scannerVideoElement]);

  const handleOpenScanner = () => {
    setScannerState("idle");
    setScannerMessage("Kamera öffnen, um den Bridge-QR-Code zu scannen.");
    setIsScannerOpen(true);
  };

  const handleRetryScan = () => {
    setScannerAttempt((value) => value + 1);
  };

  const handleEnroll = async () => {
    setIsWorking("enroll");
    try {
      // Registration also persists the device settings, so it must clear the
      // same authoritative validation as Save and Open Kiosk before anything —
      // enrollment included — is written.
      if (!(await validateDeviceSettings())) return;
      const validated = { ...form.state.values };
      const nextSession = await enrollDevice({
        insuiteRestUrl,
        solToken,
        practiceName: validated.practiceName,
        deviceName: validated.deviceName,
      });
      setSolToken("");
      await persistNonSecretSettings(nextSession, validated);
      onSessionChange(nextSession);
      setIsConnectionEditing(false);
      toast.success("Gateway-Registrierung gespeichert");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Registrierung fehlgeschlagen",
      );
    } finally {
      setIsWorking(null);
    }
  };

  const handleReplace = async () => {
    setIsWorking("replace");
    try {
      const nextSession = await replaceToken(replacementToken);
      setReplacementToken("");
      onSessionChange(nextSession);
      await onRefreshHostedReadiness();
      toast.success("Token ersetzt");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Token-Ersetzung fehlgeschlagen",
      );
    } finally {
      setIsWorking(null);
    }
  };

  const handleTest = async () => {
    setIsWorking("test");
    try {
      const nextSession = await testToken();
      onSessionChange(nextSession);
      await onRefreshHostedReadiness();
      toast.success("Token-Test abgeschlossen");
    } catch (error) {
      const nextSession = await getGatewaySession().catch(() => null);
      if (nextSession) {
        onSessionChange(nextSession);
      }
      await onRefreshHostedReadiness();
      toast.error(
        error instanceof Error ? error.message : "Token-Test fehlgeschlagen",
      );
    } finally {
      setIsWorking(null);
    }
  };

  const handleRevoke = async () => {
    setIsWorking("revoke");
    try {
      await revokeDevice();
      onSessionChange({
        enrolled: false,
        ready: false,
        revoked: true,
        expired: false,
        deviceId: null,
        practiceName: null,
        deviceName: null,
        insuiteRestUrl: null,
        token: {
          configured: false,
          maskedSuffix: null,
          fingerprint: null,
          lastVerifiedAt: null,
          lastVerificationResult: "unknown",
        },
        validationIssues: ["device_revoked"],
      });
      toast.success("Gerät widerrufen");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Widerruf fehlgeschlagen",
      );
    } finally {
      setIsWorking(null);
    }
  };

  useEffect(
    function keepPersistCallbackCurrent() {
      // The form's onSubmit runs the latest persist closure; refs are written in
      // an effect, never during render.
      persistDeviceSettingsRef.current = async () => {
        await persistNonSecretSettings(session);
      };
    },
    [persistNonSecretSettings, session],
  );

  /** Validate the device form without writing; resolves `false` when it is invalid. */
  const validateDeviceSettings = async (): Promise<boolean> => {
    await form.validateAllFields("submit");
    await form.validate("submit");
    if (form.state.isValid) return true;
    revealInvalidDeviceSettings();
    return false;
  };

  /** Validate the device form and persist it; resolves `false` when validation blocked it. */
  const submitDeviceSettings = async (): Promise<boolean> => {
    await form.handleSubmit();
    return form.state.isValid;
  };

  const handleSaveLocal = async () => {
    setIsWorking("save");
    try {
      if (!(await submitDeviceSettings())) return;
      toast.success("Browser-Einstellungen gespeichert");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Speichern der Einstellungen fehlgeschlagen",
      );
    } finally {
      setIsWorking(null);
    }
  };

  const handleOpenKiosk = async () => {
    setIsWorking("open-kiosk");
    try {
      if (!(await submitDeviceSettings())) return;
      onOpenKiosk();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Kiosk konnte nicht geöffnet werden",
      );
    } finally {
      setIsWorking(null);
    }
  };

  const focusField = useCallback((fieldId: string | null) => {
    if (!fieldId) {
      blockersRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }
    if (typeof document === "undefined") {
      return;
    }
    const element = document.getElementById(fieldId);
    if (element instanceof HTMLElement) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof (element as HTMLInputElement).focus === "function") {
        element.focus();
      }
    } else {
      blockersRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const handlePrimaryFix = useCallback(() => {
    if (!firstIssue) {
      return;
    }
    focusField(firstIssue.fieldId);
  }, [firstIssue, focusField]);

  const connectedHostname = useMemo(() => {
    const source = session.insuiteRestUrl ?? insuiteRestUrl;
    if (!source) {
      return null;
    }
    try {
      return new URL(source).hostname;
    } catch {
      return source;
    }
  }, [insuiteRestUrl, session.insuiteRestUrl]);

  const identityLine = useMemo(() => {
    const parts = [practiceName || null, deviceName || null].filter(
      (value): value is string => Boolean(value),
    );
    return parts.length > 0 ? parts.join(" · ") : "";
  }, [deviceName, practiceName]);

  return (
    <main className="frontdesk-web-setup">
      <div className="mx-auto flex max-w-5xl flex-col gap-6 p-4 md:p-6">
        <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-slate-950 text-white">
              <AscleoLogo className="w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold tracking-tight text-slate-950">
                FrontDesk+
              </p>
              {identityLine ? (
                <p
                  className="truncate text-sm text-slate-500"
                  title={identityLine}
                >
                  {identityLine}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <output
              data-testid="frontdesk-web-readiness-status"
              data-state={statusState}
              className={
                statusState === "ready"
                  ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white"
                  : statusState === "warning"
                    ? "inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white"
                    : "inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white"
              }
            >
              {statusState === "ready" ? (
                <Check className="size-3.5" />
              ) : statusState === "warning" ? (
                <TriangleAlert className="size-3.5" />
              ) : (
                <AlertTriangle className="size-3.5" />
              )}
              {statusLabel}
            </output>
            {setupStatus.ready ? (
              <Button
                type="button"
                data-testid="settings-to-kiosk-action"
                data-primary="true"
                onClick={() => void handleOpenKiosk()}
                disabled={isWorking !== null}
              >
                {isWorking === "open-kiosk" ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : null}
                Kiosk öffnen
                <ArrowRight className="ml-2 size-4" />
              </Button>
            ) : (
              <Button
                type="button"
                data-primary="true"
                onClick={handlePrimaryFix}
                disabled={isWorking !== null || !firstIssue}
              >
                {firstIssue?.action ?? "Einrichtung beheben"}
              </Button>
            )}
          </div>
        </header>

        {locked ? (
          <LockedSettingsPanel />
        ) : (
          <>
            {blockers.length > 0 ? (
              <section
                ref={blockersRef}
                data-testid="frontdesk-web-blockers"
                className="overflow-hidden rounded-xl border border-slate-200 bg-white"
              >
                <div className="border-b border-slate-200 px-4 py-3">
                  <h2 className="text-sm font-semibold text-slate-950">
                    Einrichtung abschließen, um den Kiosk zu öffnen
                  </h2>
                </div>
                <ul className="divide-y divide-slate-200">
                  {blockers.map((issue) => (
                    <li
                      key={issue.code}
                      className="flex items-center justify-between gap-3 border-l-2 border-red-600 bg-white px-4 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <AlertTriangle className="size-4 shrink-0 text-red-600" />
                        <span className="truncate text-sm font-medium text-slate-950">
                          {issue.title}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => focusField(issue.fieldId)}
                      >
                        Beheben
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {warnings.length > 0 ? (
              <section
                data-testid="frontdesk-web-warning-summary"
                className="overflow-hidden rounded-xl border border-amber-200 bg-amber-50"
              >
                <div className="border-b border-amber-200 px-4 py-3">
                  <h2 className="text-sm font-semibold text-slate-950">
                    Hinweise zu eingeschränkten Kartenfunktionen
                  </h2>
                </div>
                <ul className="divide-y divide-amber-200">
                  {warnings.map((warning) => (
                    <li
                      key={warning.code}
                      data-testid={
                        warning.code === "selected_card_reader_unavailable"
                          ? "frontdesk-web-reader-warning"
                          : undefined
                      }
                      className="flex items-start gap-2 border-l-2 border-amber-600 px-4 py-3 text-sm text-slate-700"
                    >
                      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <form
              className="space-y-8"
              data-testid="frontdesk-web-settings-form"
              onSubmit={(event) => event.preventDefault()}
            >
              <section className="space-y-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-950">
                  Verbindung
                </h2>
                {session.enrolled && !isConnectionEditing ? (
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <p className="text-sm text-slate-700">
                      Verbunden mit{" "}
                      <span className="font-medium text-slate-950">
                        {connectedHostname ?? "Bridge"}
                      </span>
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsConnectionEditing(true)}
                    >
                      Bearbeiten
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label htmlFor="insuite-url">Bridge-Adresse</Label>
                          {!isFramed && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              data-testid="frontdesk-web-start-scan"
                              onClick={handleOpenScanner}
                            >
                              <QrCode className="mr-2 size-4" />
                              Bridge-QR scannen
                            </Button>
                          )}
                        </div>
                        <Input
                          id="insuite-url"
                          data-testid="frontdesk-web-insuite-url"
                          value={insuiteRestUrl}
                          onChange={(event) =>
                            setInsuiteRestUrl(event.target.value)
                          }
                          placeholder="https://insuite.example/_rest_"
                        />
                        <p className="text-xs text-slate-500">
                          Die FrontDesk-<code>_rest_</code>-Adresse der Bridge
                          verwenden.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="soltoken">Server-Token</Label>
                        <Input
                          id="soltoken"
                          data-testid="frontdesk-web-soltoken"
                          type="password"
                          autoComplete="off"
                          value={solToken}
                          onChange={(event) => setSolToken(event.target.value)}
                          placeholder="Token für die Registrierung einfügen"
                        />
                        <p className="text-xs text-slate-500">
                          Das Gateway speichert das SOLTOKEN nach der
                          Registrierung.
                        </p>
                        {lastImportSource ? (
                          <p
                            className="text-xs text-slate-500"
                            data-testid="frontdesk-web-import-status"
                          >
                            Werte aus Bridge-QR übernommen. Prüfen und
                            „Registrieren“ drücken.
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        data-testid="frontdesk-web-enroll-device"
                        onClick={handleEnroll}
                        disabled={isWorking !== null}
                      >
                        {isWorking === "enroll" ? (
                          <Loader2 className="mr-2 size-4 animate-spin" />
                        ) : null}
                        Registrieren
                      </Button>
                    </div>
                  </>
                )}
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-950">
                  Kioskidentität
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="practice-name">Praxisname</Label>
                    <Input
                      id="practice-name"
                      data-testid="frontdesk-web-practice-name"
                      value={practiceName}
                      onChange={(event) => setPracticeName(event.target.value)}
                      placeholder="Praxis Am Markt"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device-name">Gerätename</Label>
                    <Input
                      id="device-name"
                      data-testid="frontdesk-web-device-name"
                      value={deviceName}
                      onChange={(event) => setDeviceName(event.target.value)}
                      placeholder="Empfang iPad"
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-950">
                  Kartenlesegerät
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="terminal-select">Kartenlesegerät</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="frontdesk-web-refresh-card-readers"
                      onClick={() => void loadCardReaders()}
                      disabled={
                        isWorking !== null ||
                        isLoadingTerminals ||
                        !session.enrolled ||
                        !session.token.configured
                      }
                    >
                      {isLoadingTerminals ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : null}
                      Lesegeräte aktualisieren
                    </Button>
                  </div>
                  <Select
                    value={selectedTerminalId || undefined}
                    onValueChange={(value) =>
                      setSelectedTerminalId(value ?? "")
                    }
                    disabled={
                      isLoadingTerminals ||
                      !terminals.length ||
                      !session.enrolled
                    }
                  >
                    <SelectTrigger
                      id="terminal-select"
                      data-testid="frontdesk-web-terminal-select"
                      className="w-full"
                    >
                      <SelectValue
                        placeholder={
                          isLoadingTerminals
                            ? "Kartenlesegeräte werden geladen…"
                            : terminals.length
                              ? "Kartenlesegerät auswählen"
                              : "Noch keine Kartenlesegeräte gefunden"
                        }
                      >
                        {selectedTerminalId ? selectedReaderLabel : undefined}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {terminals.map((terminal) => (
                        <SelectItem key={terminal.id} value={terminal.id}>
                          <div className="flex flex-col">
                            <span>{terminal.name}</span>
                            {terminal.subtitle ? (
                              <span className="text-xs text-muted-foreground">
                                {terminal.subtitle}
                              </span>
                            ) : null}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-950">
                  Kiosk-Einstellungen
                </h2>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="welcome-message">Begrüßungsnachricht</Label>
                    <Input
                      id="welcome-message"
                      data-testid="frontdesk-web-welcome-message"
                      value={welcomeMessage}
                      onChange={(event) =>
                        form.setFieldValue("welcomeMessage", event.target.value)
                      }
                    />
                  </div>
                  <form.Field name="pin">
                    {(field) => {
                      const showError =
                        (field.state.meta.isTouched ||
                          form.state.submissionAttempts > 0) &&
                        field.state.meta.errors.length > 0;
                      return (
                        <div className="space-y-2">
                          <Label htmlFor="settings-pin">Einstellungs-PIN</Label>
                          <Input
                            id="settings-pin"
                            data-testid="frontdesk-web-settings-pin"
                            type="password"
                            inputMode="numeric"
                            maxLength={8}
                            value={field.state.value}
                            onChange={(event) => field.handleChange(event.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={showError}
                            aria-describedby={
                              showError ? "frontdesk-web-settings-pin-error" : undefined
                            }
                          />
                          {showError ? (
                            <FieldError
                              id="frontdesk-web-settings-pin-error"
                              data-testid="frontdesk-web-settings-pin-error"
                            >
                              {getFieldErrorMessage(field.state.meta.errors[0])}
                            </FieldError>
                          ) : null}
                          <p className="text-xs text-slate-500">
                            4 bis 8 Ziffern. Die Standard-PIN wird nicht akzeptiert;
                            eine bereits gesetzte PIN bleibt erhalten, solange das Feld
                            unverändert bleibt.
                          </p>
                        </div>
                      );
                    }}
                  </form.Field>
                  <div className="space-y-2">
                    <Label htmlFor="voice-over-volume">
                      Voice-Over-Lautstärke: {voiceOverVolume}%
                    </Label>
                    <input
                      id="voice-over-volume"
                      data-testid="frontdesk-web-voice-over-volume"
                      className="w-full"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={voiceOverVolume}
                      onChange={(event) =>
                        form.setFieldValue("voiceOverVolume", Number(event.target.value))
                      }
                    />
                  </div>
                  <div className="grid gap-3">
                    <SwitchRow
                      checked={languageSelectionOnSessionStart ?? false}
                      onCheckedChange={(value) => form.setFieldValue("languageSelectionOnSessionStart", value)}
                      label="Sprachauswahl zu Beginn jeder Sitzung"
                      testId="frontdesk-web-language-entry-enabled"
                    />
                    <SwitchRow
                      checked={schedulerEnabled}
                      onCheckedChange={(value) => form.setFieldValue("schedulerEnabled", value)}
                      label="Terminbuchung anzeigen"
                      testId="frontdesk-web-scheduler-enabled"
                    />
                    <SwitchRow
                      checked={prescriptionEnabled}
                      onCheckedChange={(value) => form.setFieldValue("prescriptionEnabled", value)}
                      label="Rezeptanfragen anzeigen"
                      testId="frontdesk-web-prescription-enabled"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="frontdesk-web-save-local-settings"
                      onClick={handleSaveLocal}
                      disabled={isWorking !== null}
                    >
                      {isWorking === "save" ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : null}
                      Browser-Einstellungen speichern
                    </Button>
                  </div>
                </div>
              </section>
            </form>

            <Collapsible
              data-testid="frontdesk-web-advanced-section"
              defaultOpen={shouldAdvancedStartOpen(session)}
              className="overflow-hidden rounded-xl border border-slate-200"
            >
              <CollapsibleTrigger data-testid="frontdesk-web-advanced-trigger" className="flex w-full items-center justify-between gap-2 bg-white px-4 py-3 text-left">
                <span className="text-sm font-semibold uppercase tracking-wide text-slate-950">
                  Erweitert
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 border-t border-slate-200 bg-white px-4 py-4">
                <p className="text-sm text-slate-600">
                  {session.token.maskedSuffix ?? "Nicht konfiguriert"} • letzter
                  Test: {formatVerificationResult(session.token.lastVerificationResult)}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="replacement-token">Ersatz-Token</Label>
                  <Input
                    id="replacement-token"
                    data-testid="frontdesk-web-replacement-token"
                    type="password"
                    autoComplete="off"
                    value={replacementToken}
                    onChange={(event) =>
                      setReplacementToken(event.target.value)
                    }
                    placeholder="Ersatz-Token einfügen"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="frontdesk-web-replace-token"
                    onClick={handleReplace}
                    disabled={isWorking !== null || !replacementToken}
                  >
                    {isWorking === "replace" ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : null}
                    Ersetzen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="frontdesk-web-test-token"
                    onClick={handleTest}
                    disabled={isWorking !== null || !session.token.configured}
                  >
                    {isWorking === "test" ? (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    ) : null}
                    Testen
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    data-testid="frontdesk-web-revoke-device"
                    onClick={handleRevoke}
                    disabled={isWorking !== null || !session.enrolled}
                  >
                    <LogOut className="mr-2 size-4" />
                    Widerrufen
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <BrowserSetupScannerDialog
              open={isScannerOpen}
              onOpenChange={setIsScannerOpen}
              scannerState={scannerState}
              scannerMessage={scannerMessage}
              onRetryScan={handleRetryScan}
              onUseManualSetup={() => setIsScannerOpen(false)}
              setVideoElement={setScannerVideoElement}
            />
          </>
        )}
      </div>
    </main>
  );
}

function LockedSettingsPanel() {
  return (
    <section
      className="flex flex-col items-center justify-center gap-3 py-16"
      data-testid="frontdesk-web-settings-lock"
    >
      <div className="flex size-12 items-center justify-center rounded-xl bg-slate-950 text-white">
        <AscleoLogo className="w-7" />
      </div>
      <p className="text-lg font-semibold tracking-tight text-slate-950">
        FrontDesk+
      </p>
      <p className="text-sm text-slate-500">Geschützte Einstellungen</p>
    </section>
  );
}

function SwitchRow({
  checked,
  onCheckedChange,
  label,
  testId,
}: {
  readonly checked: boolean;
  readonly onCheckedChange: (value: boolean) => void;
  readonly label: string;
  readonly testId: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border p-3">
      <Label>{label}</Label>
      <Switch
        data-testid={testId}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

function toTerminalOption(reader: BrowserCardReader): BrowserTerminalOption {
  return {
    id: reader.id,
    name: reader.name,
    subtitle: [reader.workplaceName, reader.mandantName]
      .filter((value): value is string => Boolean(value?.trim()))
      .join(" • "),
  };
}

function readGatewayMessage(payload: unknown, status: number): string {
  if (typeof payload === "object" && payload !== null) {
    const message =
      ("message" in payload && typeof payload.message === "string"
        ? payload.message
        : null) ??
      ("error" in payload && typeof payload.error === "string"
        ? payload.error
        : null);

    if (message) {
      return message;
    }
  }

  return `Gateway-Anfrage fehlgeschlagen (${status})`;
}

function getScannerErrorMessage(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError") {
      return "Kamerazugriff wurde verweigert.";
    }

    if (error.name === "NotFoundError") {
      return "Auf diesem Gerät wurde keine Kamera gefunden.";
    }
  }

  return "Der Browser konnte die QR-Scanner-Vorschau nicht starten.";
}

function formatVerificationResult(
  result: GatewaySession["token"]["lastVerificationResult"],
): string {
  switch (result) {
    case "success":
      return "erfolgreich";
    case "failed":
      return "fehlgeschlagen";
    case "skipped":
      return "übersprungen";
    case "unknown":
      return "unbekannt";
  }
}
